import SETTINGS from '../settings';
import { apiClientPostRequest } from '../lib/backendRequest';

const authApi = countryCode => ({
  /**
   * @param {string} email
   * @param {string} password
   * @returns {Promise<{data: object, error}>}
   */
  async signIn(email, password) {
    try {
      const response = await apiClientPostRequest(
        'api/v1/auth/signin',
        {
          auth: {
            email,
            password,
            provider: 0,
          },
          country_alpha2: countryCode,
        },
        {},
        {
          logBodyDuringIssue: false,
        },
      );

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return {
        error: {
          code: e.code || 500,
          body: e.body || undefined,
        },
      };
    }
  },

  /**
   * @param {string} email
   * @param {string} password
   * @returns {Promise<{data, error}>}
   */
  async signUp(email, password) {
    try {
      const response = await apiClientPostRequest(
        'api/v1/auth/signup',
        {
          auth: {
            email,
            password,
            provider: 0,
          },
          country_alpha2: countryCode,
        },
        {},
        {
          logBodyDuringIssue: false,
        },
      );

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return {
        error: {
          code: e.code || 500,
          body: e.body || undefined,
        },
      };
    }
  },

  /**
   * @param {string} email
   * @returns {Promise<{data: boolean, error}>}
   */
  async resetPassword(email) {
    try {
      const response = await apiClientPostRequest('api/v1/users/reset-password', {
        email,
        redirect_uri: SETTINGS.HOST,
      });

      await response.json();

      return { data: true };
    } catch (e) {
      // when API returns 404 - it means that user with this email doesn't exist, but we don't want to show this info to the user
      if (e.code === 404) {
        return { data: true };
      }
      return { error: e.code };
    }
  },

  /**
   * @param {string} userId
   * @param {string} name
   * @param {string} email
   * @returns {Promise<{data: object, error}>}
   */
  async signInWithFacebook(userId, name, email) {
    try {
      const response = await apiClientPostRequest('api/v1/auth/signup', {
        auth: {
          user_id: userId,
          provider: 5,
          name,
          email,
        },
        country_alpha2: countryCode,
      });

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e.code };
    }
  },

  /**
   * @param {string} customerId
   * @param {string} amazonToken
   * @param {string} email
   * @returns {Promise<{data: object, error}>}
   */
  async signInWithAmazon(customerId, amazonToken, email) {
    try {
      const response = await apiClientPostRequest('api/v1/auth/signup', {
        auth: {
          token: amazonToken,
          email,
          user_id: customerId,
          provider: 4,
        },
        country_alpha2: countryCode,
      });

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e.code };
    }
  },

  /**
   * When infoONly is true, data only returns object provider. Don't use for sign in. The main purpose is to add Ancestry provider to the existing account.
   * Returns { provider }
   *
   * When infoOnly is false, it's classic signIn api call.
   * Returns { userData }
   *
   * @param {string} authorizationCode
   * @param {boolean} infoOnly
   */
  async signInWithFamilyTreeProvider(type, authorizationCode, infoOnly = false) {
    try {
      const response = await apiClientPostRequest(`api/v1/auth/${type}`, {
        code: authorizationCode,
        info_only: infoOnly,
      });

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e.code };
    }
  },

  /**
   * Returns { }
   *
   * @param {string} email
   */
  async checkEmail(email) {
    try {
      const response = await apiClientPostRequest('api/v1/auth/check-email', {
        email,
      });

      await response.json();

      return { data: {} };
    } catch (e) {
      return { error: e.code };
    }
  },

  /**
   * Returns { }
   *
   * @param {string} token original user`s access_token
   * @param {string} code instagram code
   * @param {string} redirectUrl
   */
  async addInstagramProvider(token, code, redirectUrl) {
    try {
      const response = await apiClientPostRequest(
        'api/v1/auth/instagram',
        {
          code,
          redirect_url: redirectUrl,
          country_alpha2: countryCode,
        },
        {
          Authorization: `Token ${token}`,
        },
      );

      const json = await response.json();

      return { data: json.data };
    } catch (error) {
      return { error };
    }
  },
});

export default authApi;
